import { createReducer, on } from '@ngrx/store'
import { WritableDraft, produce } from 'immer'
import { last } from 'lodash/fp'
import { v4 as uuidV4 } from 'uuid'

import { ScriptingLanguage } from '@flowx/designer/modules/core/dictionary/process.dictionary'
import { UiFlowDesignerService } from '@flowx/designer/modules/new-ai/agents/designer/services/ui-flow-designer.service'
import {
  UiDesignerComputedModalActions,
  UiDesignerConfigPanelActions,
  UiDesignerExpressionModalActions,
} from '@flowx/designer/modules/ui-designer/ui-designer.actions'
import { ProcessDataModelActions } from '@flowx/designer/shared/store/actions/process-data-model.actions'
import { TemplateConfigActions } from '@flowx/designer/shared/store/actions/template-config.actions'

// eslint-disable-next-line import/no-cycle
import {
  AiActions,
  AiPermissionActions,
  BreadcrumbActions,
  CommandPaletteActions,
  CtaActions,
  FileUploadConfigActions,
  FloatingWindowActions,
  KeyboardActions,
  NodeMenuActions,
  SequenceMenuActions,
} from './ai.actions'
import { AiState, initialState } from './ai.state'
import {
  onGetAnswerEventStream as onAnalystEventStream,
  onExtractProcessFromDocumentSuccess,
  onGenerateProcessSuccess,
} from './handlers/agents/analyst.handler'
import { onGetAnswerEventStream as onArchitectEventStream } from './handlers/agents/architect.handler'
import {
  onGetAnswerEventStream as onAssistantEventStream,
  onGetAnswerEventStreamOld as onAssistantEventStreamOld,
} from './handlers/agents/assistant.handler'
import {
  onAuditorAnswerEventStream,
  onAuditorStreamBegin,
  onAuditorStreamCompleted,
} from './handlers/agents/auditor.handler'
import { onAgentFailure } from './handlers/agents/common.handler'
import {
  onAutomateProcess,
  onGetAnswerEventStream as onCoordinatorEventStream,
  onDeletePlan,
  onGeneratedActionHandler,
  onGeneratedPromptForAgentSuccess,
  onInvokeStep,
  onNoAction,
  onRefineList,
  onSelectInterruptOption,
  onUpdateStep,
  onYesAction,
} from './handlers/agents/coordinator.handler'
import {
  onGetAnswerEventStream as onDesignerEventStream,
  onDiscardUIDesignerDraft,
  onExtractUIFromDocumentSuccess,
  onGenerateUiSuccess,
  onKeepTemplateConfigDraftSuccess,
  onMapTemplateConfigComponentsToDataModel,
  onUiDesignerNodeAiClick,
  onUpdateDataModelOnLastMessage,
} from './handlers/agents/designer.handler'
import {
  onAddCommentSuccess,
  onGetAnswerEventStream as onDeveloperEventStream,
  onExplainBusinessRuleSuccess,
  onExtractBusinessProcessFromDocumentSuccess,
  onFixBusinessRuleErrorSuccess,
  onFixBusinessRuleWarningsSuccess,
  onGenerateBusinessRuleSuccess,
  onGenerateExpressionSuccess,
  onGenerateGatewayConditionSuccess,
  onImproveCodeSuccess,
} from './handlers/agents/developer.handler'
import {
  onDiscardDatasourceDraft,
  onGetAnswerEventStream as onIntegratorGetAnswerEventStream,
  onKeepAgentDraftSuccess,
  onKeepDatasourceDraftSuccess,
} from './handlers/agents/integrator.handler'
import { setMinimizedFalse } from './handlers/ui/interaction.handler'

import { AiAnalystUtils } from '../agents/analyst/services/analyst.service'
import { AiAnalystActions } from '../agents/analyst/store/analyst.actions'
import { AiArchitectActions } from '../agents/architect/store/architect.actions'
import { AiAssistantActions } from '../agents/assistant/store/assistant.actions'
import { AiAuditorActions } from '../agents/auditor/store/auditor.actions'
import { AiCoordinatorActions } from '../agents/coordinator/store/coordinator.actions'
import { AiDesignerActions } from '../agents/designer/store/designer.actions'
import {
  AiCodeEditorContextMenuActions,
  AiDeveloperActions,
} from '../agents/developer/store/developer.actions'
import { AiDocumentsActions } from '../agents/documents/store/documents.actions'
import { AiInspectorActions } from '../agents/inspector/api/store/inspector.actions'
import { AiIntegratorActions } from '../agents/integrator/store/integrator.actions'
import {
  AiAgent,
  Message,
  MessageType,
  scriptingLanguageWithGeneration,
} from '../common/ai.dictionary'

export const aiReducer = createReducer(
  initialState,

  // Configs
  on(AiPermissionActions.loadAiPermissionsSuccess, (state: AiState, { permissions }) =>
    produce(state, (draft: AiState) => {
      draft.permissions = permissions
    })
  ),
  on(FileUploadConfigActions.loadFileConfigSuccess, (state: AiState, { fileUploadConfig }) =>
    produce(state, (draft: AiState) => {
      draft.fileUploadConfig = fileUploadConfig
    })
  ),
  on(AiActions.fabMoved, (state: AiState, { position }) =>
    produce(state, (draft: AiState) => {
      draft.fabPosition = position
    })
  ),
  on(AiActions.businessRuleContextChanged, (state: AiState, { businessRuleContext }) =>
    produce(state, (draft: AiState) => {
      draft.businessRuleContext = businessRuleContext
      if (!state.zenMode) {
        if (
          state.selectedAgent &&
          !scriptingLanguageWithGeneration.includes(
            businessRuleContext?.language as ScriptingLanguage
          )
        ) {
          draft.minimized = true
          closeChat(draft)
        }
      }
    })
  ),
  on(AiActions.businessRuleTestDataChanged, (state: AiState, { businessRuleTestData }) =>
    produce(state, (draft: AiState) => {
      draft.generatedTestData = businessRuleTestData
    })
  ),
  on(AiActions.currentEnumeration, (state: AiState, { enumeration }) =>
    produce(state, (draft: AiState) => {
      draft.currentEnumeration = enumeration
    })
  ),

  on(AiActions.currentUI, (state: AiState, { templateConfig }) =>
    produce(state, (draft: AiState) => {
      draft.currentUI = templateConfig
    })
  ),

  on(AiActions.currentFullUI, (state: AiState, { templateConfig }) =>
    produce(state, (draft: AiState) => {
      draft.currentFullUI = templateConfig
    })
  ),

  on(AiActions.currentNotification, (state: AiState, { notification }) =>
    produce(state, (draft: AiState) => {
      draft.currentNotification = notification
    })
  ),

  on(AiActions.currentDataModel, (state: AiState, { entities }) =>
    produce(state, (draft: AiState) => {
      draft.currentDataModel = entities
    })
  ),
  on(AiActions.setPlanSidebarVisible, (state: AiState, { isPlanSidebarVisible }) =>
    produce(state, (draft: AiState) => {
      draft.isPlanSidebarVisible = isPlanSidebarVisible
    })
  ),
  on(AiActions.setPlanEnabled, (state: AiState, { isPlanEnabled }) =>
    produce(state, (draft: AiState) => {
      draft.isPlanEnabled = isPlanEnabled
    })
  ),
  on(AiActions.setChatSidebarVisible, (state: AiState, { isChatSidebarVisible }) =>
    produce(state, (draft: AiState) => {
      draft.isChatSidebarVisible = isChatSidebarVisible
    })
  ),
  on(AiActions.setDocumentsSidebarVisible, (state: AiState, { isDocumentsSidebarVisible }) =>
    produce(state, (draft: AiState) => {
      draft.isDocumentsSidebarVisible = isDocumentsSidebarVisible
    })
  ),

  on(AiActions.setArchivedSidebarVisible, (state: AiState, { isArchivedSidebarVisible }) =>
    produce(state, (draft: AiState) => {
      draft.isArchivedSidebarVisible = isArchivedSidebarVisible
    })
  ),

  on(UiDesignerExpressionModalActions.expressionUpdate, (state: AiState, { expression }) =>
    produce(state, (draft: AiState) => {
      draft.expressionContext = {
        expression,
      }
    })
  ),
  on(AiActions.actionEditDestroyed, (state: AiState) =>
    produce(state, (draft: AiState) => {
      draft.businessRuleContext = null

      if (state.selectedAgent === AiAgent.DEVELOPER) {
        draft.minimized = true
        closeChat(draft)
      }
    })
  ),
  on(
    UiDesignerExpressionModalActions.componentDestroy,
    UiDesignerComputedModalActions.componentDestroy,
    (state: AiState) =>
      produce(state, (draft: AiState) => {
        draft.expressionContext = null
        draft.businessRuleContext = null
      })
  ),

  // Navigation
  on(AiActions.pageAgentChanged, (state: AiState) =>
    produce(state, (draft: AiState) => {
      if (state.zenMode) {
      } else {
        draft.minimized = true
        closeChat(draft)
      }
    })
  ),
  on(AiActions.setSelectedAgent, (state: AiState, { selectedAgent }) =>
    produce(state, (draft: AiState) => {
      draft.selectedAgent = selectedAgent
    })
  ),

  on(AiActions.fabClick, (state: AiState) =>
    produce(state, (draft: AiState) => {
      draft.zenMode = true
    })
  ),
  on(
    TemplateConfigActions.singleSelectTemplateConfig, // changed template config selected node
    FloatingWindowActions.minimizeClick,
    AiActions.outsideOfAiChatClick,
    (state) =>
      produce(state, (draft: AiState) => {
        draft.minimized = true

        const isLoading = state.pendingAiResponse || state.pendingKeepDraftSave

        if (!isLoading && hasOnlySystemMsg(state.messages)) {
          closeChat(draft)
        }
      })
  ),

  // Files
  on(AiActions.fileUploadSuccess, (state: AiState, { files }) =>
    produce(state, (draft: AiState) => {
      draft.uploadedFiles = files
    })
  ),
  on(AiActions.fileRemoveClick, (state: AiState, { id }) =>
    produce(state, (draft: AiState) => {
      draft.uploadedFiles = state.uploadedFiles.filter((file) => file.id !== id)
    })
  ),

  on(AiAuditorActions.extractComplianceFromDocumentSuccess, (state: AiState, { response }) =>
    produce(state, (draft: AiState) => {
      draft.messages.push({
        message: `<b>Look what I found in the compliance document</b>: <p> <i>${response.response}</i> </p> I will use this information to check the bussines process for you.`,
        type: MessageType.AiMsg,
        commited: true,
      })

      if (response.type_of_action === 'compliance_check_ui') {
        AiAuditorActions.checkUI({ prompt: response.response })
      } else {
        AiAuditorActions.checkProcess({ prompt: response.response })
      }
    })
  ),

  on(AiAnalystActions.extractProcessFromDocumentSuccess, onExtractProcessFromDocumentSuccess),

  on(
    AiDeveloperActions.extractBusinessRuleFromDocumentSuccess,
    onExtractBusinessProcessFromDocumentSuccess
  ),

  on(AiDesignerActions.extractUiFromDocumentSuccess, onExtractUIFromDocumentSuccess),

  on(AiAuditorActions.checkProcessSuccess, (state: AiState, { response }) =>
    produce(state, (draft: AiState) => {
      draft.messages.push({
        message: `<b>I have checked the process and found the following issues:</b> <p> <i>${response.response}</i> </p> `,
        actions: {
          show: true,
        },
        type: MessageType.AiMsg,
        commited: true,
      })
      draft.hasAudit = true
      draft.generatedAiRecommendation = response.response
    })
  ),
  on(AiAuditorActions.checkUISuccess, (state: AiState, { response }) =>
    produce(state, (draft: AiState) => {
      draft.messages.push({
        message: `<b>I have checked the UI and found the following issues:</b> <p> <i>${response.response}</i> </p> `,
        actions: {
          show: true,
        },
        type: MessageType.AiMsg,
        commited: true,
      })
      draft.hasAudit = true
      draft.generatedAiRecommendation = response.response
    })
  ),
  // ── Auditor · SSE streaming path ───────────────────────────────────────
  // Stream begin: seed the user prompt + an empty AI message that the
  // per-chunk handler will fill in.
  on(AiAuditorActions.checkUIStream, onAuditorStreamBegin),
  on(AiAuditorActions.checkProcessStream, onAuditorStreamBegin),
  // Per-chunk: incremental rendering of the audit text.
  on(AiAuditorActions.getAnswerEventStream, onAuditorAnswerEventStream),
  // Stream end (or cancel): commit the in-flight message, clear flags.
  on(AiAuditorActions.streamCompleted, onAuditorStreamCompleted),
  // Documents

  on(AiDocumentsActions.loadDocuments, (state) =>
    produce(state, (draft: AiState) => {
      draft.pendingDocumentsLoading = true
    })
  ),
  on(AiDocumentsActions.loadDocumentsSuccess, (state: AiState, { documents }) =>
    produce(state, (draft: AiState) => {
      draft.pendingDocumentsLoading = false
      if (!state.zenMode) {
        if (documents.length) {
          draft.messages.push({
            message: state.permissions.canUploadDocuments
              ? `Or you can upload a new document:`
              : '',
            documents: {
              message: 'Here are the existing documents on this project:',
              files: documents,
            },
            type: MessageType.SystemMsg,
            actions: {
              documentUpload: state.permissions.canUploadDocuments,
            },
            commited: true,
          })
          draft.currentDocuments = documents
        } else {
          draft.messages.push({
            message: `You don't have any documents uploaded yet. ${state.permissions.canUploadDocuments ? 'Upload a document to get started.' : 'Try generating from an prompt'}`,
            type: MessageType.SystemMsg,
            actions: {
              documentUpload: state.permissions.canUploadDocuments,
            },
            commited: true,
          })
          draft.currentDocuments = []
        }
      } else {
        draft.currentDocuments = documents.filter((document) => document.status === 'Evaluated')
      }
    })
  ),
  on(AiDocumentsActions.uploadDocuments, (state: AiState, { documents }) =>
    produce(state, (draft: AiState) => {
      documents.forEach((document) => {
        document.session_id = state.sessionId
      })
      draft.uploadingDocuments = documents
      draft.isUploadingDocuments = true
    })
  ),
  on(AiDocumentsActions.uploadDocumentSSEStatus, (state: AiState, { message, subtitle }) =>
    produce(state, (draft: AiState) => {
      draft.currentAITaskMessage = subtitle
      draft.documentUploadMessage = message
    })
  ),
  on(AiDocumentsActions.uploadDocumentsSuccess, (state: AiState, { documents, pageAgent }) =>
    produce(state, (draft: AiState) => {
      draft.uploadingDocuments = []
      draft.currentDocuments = [...state.currentDocuments, ...documents]
      draft.isUploadingDocuments = false
      draft.documentUploadMessage = null
      if (documents.length > 0) {
        draft.selectedDocument = documents[0]
        draft.messages.push({
          message: `Here is a summary of your uploaded document:`,
          generators: {
            images: documents[0].images,
            summary: documents[0].summary,
            description: documents[0].description,
            documentId: documents[0].documentId,
            items: documents[0][draft.selectedAgent || pageAgent] ?? [],
            agent: draft.selectedAgent || pageAgent,
          },
          type: MessageType.SystemMsg,
          commited: true,
        })
      }
    })
  ),
  on(AiDocumentsActions.uploadDocumentsFailure, (state: AiState) =>
    produce(state, (draft: AiState) => {
      draft.uploadingDocuments = []
      draft.documentUploadMessage = null
      draft.messages.push({
        message: 'Seems like something went wrong with your request. Can you please try again?',
        type: MessageType.SystemMsg,
        commited: true,
      })
    })
  ),
  on(
    AiActions.documentClick,
    AiActions.selectExtractedDocument,
    (state: AiState, { document, pageAgent }) =>
      produce(state, (draft: AiState) => {
        draft.selectedDocument = document

        draft.messages.push({
          message: `Here is a summary of your selected document:`,
          generators: {
            images: document.images,
            summary: document.summary,
            description: document.description,
            documentId: document.documentId,
            items: document[pageAgent || state.selectedAgent || 'analyst'] ?? [],
            agent: pageAgent || state.selectedAgent || AiAgent.ANALYST,
          },
          type: MessageType.SystemMsg,
          commited: true,
        })
      })
  ),

  on(
    AiActions.contextDocumentCloseClick,
    AiActions.closeContextDocumentConfirmed,
    (state: AiState) =>
      produce(state, (draft: AiState) => {
        draft.selectedDocument = null
        draft.selectedDocumentContext = null
      })
  ),
  on(AiActions.generateFromDocumentClick, (state: AiState, { documentContextReference }) =>
    produce(state, (draft: AiState) => {
      draft.selectedDocumentContext = documentContextReference
    })
  ),
  on(AiActions.setAppIdAndVersionId, (state: AiState, { appId, appName, appVersionId }) =>
    produce(state, (draft: AiState) => {
      draft.appId = appId
      draft.appName = appName
      draft.appVersionId = appVersionId
    })
  ),

  // Commands & CTA
  // AGENT ASSISTANT
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  on(CommandPaletteActions.askAssistantCommand, (state: AiState, { question, processDoc }) =>
    produce(state, (draft: AiState) => {
      openChat(draft, {
        selectedAgent: AiAgent.ASSISTANT,
        initialMessage: {
          message: question,
          type: MessageType.UserMsg,
        },
      })
    })
  ),
  // AGENT ANALYST
  on(
    CommandPaletteActions.generateNewProcessCommand,
    CtaActions.processDesignerCtaClick,
    (state: AiState) =>
      produce(state, (draft: AiState) => {
        if (canSelectAgent(state)) {
          const draftMessage = {
            message: 'Describe the business process content so I can generate it.',
            actions: {
              checkDocumentContext: state.permissions.canViewDocuments,
              imageUpload: state.permissions.canUploadDocuments,
              processUpload: true,
            },
            type: MessageType.SystemMsg,
          }
          draft.draftMessage = {
            message: draftMessage,
            payload: {
              action_type: 'business_process',
              context: null,
              continue_editing: false,
            },
            selectedAgent: AiAgent.ANALYST,
          }
        }
      })
  ),
  on(SequenceMenuActions.generateProcess, (state) =>
    produce(state, (draft: AiState) => {
      if (canSelectAgent(state)) {
        openChat(draft, {
          selectedAgent: AiAgent.ANALYST,
          initialMessage: {
            message:
              'Hello! I am here to help you expand the process between these 2 nodes. Please describe the changes below.',
            type: MessageType.SystemMsg,
          },
          isFileUploadAllowed: false,
        })
      }
    })
  ),
  on(SequenceMenuActions.generateProcess, (state, { sequenceId }) =>
    produce(state, (draft: AiState) => {
      if (canSelectAgent(state)) {
        openChat(draft, {
          selectedAgent: AiAgent.ANALYST,
          initialMessage: {
            message:
              'Hello! I am here to help you expand the process between these 2 nodes. Please describe the changes below.',
            type: MessageType.SystemMsg,
          },
          isFileUploadAllowed: false,
        })
        draft.startedFromSequence = sequenceId
      }
    })
  ),
  on(
    CommandPaletteActions.generateExistingProcessCommand,
    NodeMenuActions.generateProcessNodeMenuAction,
    (state: AiState) =>
      produce(state, (draft: AiState) => {
        if (canSelectAgent(state)) {
          const draftMessage = {
            message:
              'Describe the changes you want as detailed as possible including nodes and swimlane names.',
            actions: {
              checkDocumentContext: state.permissions.canViewDocuments,
              imageUpload: state.permissions.canUploadDocuments,
              processUpload: true,
            },
            type: MessageType.SystemMsg,
          }
          draft.draftMessage = {
            message: draftMessage,
            payload: {
              action_type: 'business_process',
              context: null,
              continue_editing: false,
            },
            selectedAgent: AiAgent.ANALYST,
          }
        }
      })
  ),
  // AGENT AUDITOR
  on(CommandPaletteActions.checkExistingProcessCommand, (state: AiState) =>
    produce(state, (draft: AiState) => {
      if (canSelectAgent(state)) {
        const draftMessage = {
          message:
            'You can use your compliance documents to audit your process. Select an existing document or upload a new one.',
          actions: {
            checkDocumentContext: true,
            imageUpload: false,
            processUpload: false,
          },
          type: MessageType.SystemMsg,
        }
        draft.draftMessage = {
          message: draftMessage,
          payload: {
            action_type: 'compliance_check',
            context: null,
            continue_editing: false,
          },
          selectedAgent: AiAgent.AUDITOR,
        }
      }
    })
  ),
  on(AiInspectorActions.getWarningsCountSuccess, (state: AiState, { count }) =>
    produce(state, (draft: AiState) => {
      draft.process_warnings_no = count
    })
  ),
  on(AiInspectorActions.getRulesSuccess, (state: AiState, { rules }) =>
    produce(state, (draft: AiState) => {
      draft.process_rules = rules
    })
  ),
  on(AiInspectorActions.getWarningsMessagesSuccess, (state: AiState, { warnings }) =>
    produce(state, (draft: AiState) => {
      draft.process_warnings = warnings
    })
  ),
  on(AiInspectorActions.inspectProcessStream, (state: AiState, { response }) =>
    produce(state, (draft: AiState) => {
      const { message } = response || {}
      if (!message) {
        return
      }
      // Update progress in logs
      if (message.debug) {
        draft.logs.unshift({
          agent: 'Inspector',
          message: message.debug.info || '',
          message_type: 'INFO',
          timestamp: new Date().toISOString(),
          status: message.debug.status,
          step: message.debug.step,
        })
        draft.currentAITaskMessage = message.debug.info
      }
      // Capture final report
      if (message.end?.operation === 'inspection_complete') {
        const report = message.end.answer || {}
        draft.process_warnings_no = report.summary?.total || 0
      }
    })
  ),
  on(AiActions.setAutonomyLevel, (state: AiState, { autonomyLevel }) =>
    produce(state, (draft: AiState) => {
      draft.autonomyLevel = autonomyLevel
    })
  ),
  on(AiActions.setProcessName, (state: AiState, { processId, processName }) =>
    produce(state, (draft: AiState) => {
      const processChanged = state.processId !== null && state.processId !== processId

      draft.processId = processId
      draft.processName = processName

      if (processChanged) {
        newChatSession(draft)
        draft.messages = []
        draft.logs = []
        draft.reasoningLogs = []
        draft.selectedAgent = null
        draft.sessionId = null
        draft.currentPlans = null
        draft.selectedPlan = null
        draft.currentPlanStep = null
        draft.waitingForHumanApproval = false
        draft.currentAITaskMessage = null
        draft.generatedIntent = null
        draft.pendingRouteToCoordinator = null
        draft.pendingRouteToAssistant = null
        draft.pendingRouteToAnalyst = null
        draft.pendingRouteToDeveloper = null
        draft.pendingRouteToDesigner = null
        draft.pendingRouteToArchitect = null
        draft.pendingRouteToIntegrator = null
      }
    })
  ),
  on(AiActions.setCurrentSystemContext, (state: AiState, { currentSystem }) =>
    produce(state, (draft: AiState) => {
      draft.currentSystemContext = currentSystem
    })
  ),
  on(AiCoordinatorActions.routeToAgentConsumed, (state: AiState) =>
    produce(state, (draft: AiState) => {
      draft.pendingRouteToAssistant = null
      draft.pendingRouteToDesigner = null
      draft.pendingRouteToAnalyst = null
      draft.pendingRouteToDeveloper = null
      draft.pendingRouteToCoordinator = null
      draft.pendingRouteToArchitect = null
      draft.pendingRouteToIntegrator = null
      draft.pendingRouteToAuditor = null
    })
  ),
  on(AiCoordinatorActions.getAllPlansSuccess, (state: AiState, { plans }) =>
    produce(state, (draft: AiState) => {
      draft.currentPlans = plans
    })
  ),
  on(AiCoordinatorActions.getPlanSuccess, (state: AiState, { plan }) =>
    produce(state, (draft: AiState) => {
      draft.selectedPlan = plan.plan
      draft.selectedPlan.id = plan.id
      draft.isPlanEnabled = true
      // Enrich steps with parentAgent and step_destination when loading from server
      if (draft.selectedPlan?.actions) {
        for (const action of draft.selectedPlan.actions) {
          for (const step of action.steps) {
            if (!step.parentAgent) {
              step.parentAgent = action.agent
            }
            // Set step_destination if missing (server may not persist it)
            if (!step.step_destination) {
              if (action.agent === AiAgent.ANALYST) {
                // eslint-disable-next-line no-extra-semi
                ;(step as any).step_destination = {
                  routerParams: [
                    'projects',
                    draft.appId,
                    'config',
                    draft.appVersionId,
                    'processes',
                    draft.processId,
                    'diagram',
                  ],
                  type: 'Process',
                  name: draft.processName || 'CurrentProcess',
                  id: draft.processId,
                }
              }
              if (action.agent === AiAgent.INTEGRATOR) {
                // eslint-disable-next-line no-extra-semi
                ;(step as any).step_destination = {
                  routerParams: [
                    'projects',
                    draft.appId,
                    'config',
                    draft.appVersionId,
                    'agent-builder',
                    'agents',
                  ],
                  type: 'Project',
                  name: draft.appName || 'CurrentProject',
                  id: draft.appId,
                }
              }
              if (action.agent === AiAgent.ARCHITECT) {
                // eslint-disable-next-line no-extra-semi
                ;(step as any).step_destination = {
                  routerParams: [
                    'projects',
                    draft.appId,
                    'config',
                    draft.appVersionId,
                    'processes',
                    draft.processId,
                    'data-model',
                  ],
                  type: 'Process',
                  name: draft.processName || 'CurrentProcess',
                  id: draft.processId,
                }
              }
            }
          }
        }
      }
    })
  ),
  on(CommandPaletteActions.checkExistingUICommand, (state: AiState, { userTaskNavigationAreaId }) =>
    produce(state, (draft: AiState) => {
      if (canSelectAgent(state)) {
        draft.selectedDesignerUserTaskAreaId = userTaskNavigationAreaId
        const draftMessage = {
          message:
            'You can use your compliance documents to audit your user interface. Select an existing document or upload a new one.',
          actions: {
            checkDocumentContext: true,
            imageUpload: false,
            processUpload: false,
          },
          type: MessageType.SystemMsg,
        }
        draft.draftMessage = {
          message: draftMessage,
          payload: {
            action_type: 'compliance_check',
            context: null,
            continue_editing: false,
          },
          selectedAgent: AiAgent.AUDITOR,
        }
      }
    })
  ),
  // AGENT DESIGNER
  on(
    CommandPaletteActions.generateNewUICommand,
    CtaActions.uiDesignerCtaClick,
    (state: AiState, { userTaskNavigationAreaId }) =>
      produce(state, (draft: AiState) => {
        if (canSelectAgent(state)) {
          draft.selectedDesignerUserTaskAreaId = userTaskNavigationAreaId
          const draftMessage = {
            message: 'Describe the user interface content so I can generate items for it.',
            actions: {
              checkDocumentContext: state.permissions.canViewDocuments,
              imageUpload: state.permissions.canUploadDocuments,
              processUpload: false,
            },
            type: MessageType.SystemMsg,
          }
          draft.draftMessage = {
            message: draftMessage,
            payload: {
              action_type: 'user_interface',
              context: null,
              continue_editing: false,
            },
            selectedAgent: AiAgent.DESIGNER,
          }
        }
      })
  ),
  // AGENT DESIGNER — UI Flow context (no user task / navigation area)
  on(CtaActions.uiFlowDesignerCtaClick, (state: AiState) =>
    produce(state, (draft: AiState) => {
      if (canSelectAgent(state)) {
        const draftMessage = {
          message:
            'Describe the UI Flow you want me to generate — I can create multi-page applications.',
          actions: {
            checkDocumentContext: state.permissions.canViewDocuments,
            imageUpload: state.permissions.canUploadDocuments,
            processUpload: false,
          },
          type: MessageType.SystemMsg,
        }
        draft.draftMessage = {
          message: draftMessage,
          payload: {
            action_type: 'ui_flow',
            context: null,
            continue_editing: false,
          },
          selectedAgent: AiAgent.DESIGNER,
        }
      }
    })
  ),
  on(AiDesignerActions.uiDesignerNodeAiClick, onUiDesignerNodeAiClick),
  on(
    AiActions.setCurrentNodeToGenerateUiFor,
    (state: AiState, { userTaskNavigationAreaId, selectedTemplateConfigId }) =>
      produce(state, (draft: AiState) => {
        draft.selectedDesignerUserTaskAreaId = userTaskNavigationAreaId
        draft.selectedDesignerTemplateConfigId =
          selectedTemplateConfigId > 0 ? selectedTemplateConfigId : null
      })
  ),
  on(NodeMenuActions.generateUiNodeMenuActionTransitionEnd, (state: AiState) =>
    produce(state, (draft: AiState) => {
      if (!draft.zenMode) {
        if (canSelectAgent(state)) {
          draft.zenMode = false
          const draftMessage = {
            message: 'Describe the enumeration content so I can generate items for it.',
            actions: {
              checkDocumentContext: state.permissions.canViewDocuments,
              imageUpload: state.permissions.canUploadDocuments,
              processUpload: false,
            },
            type: MessageType.SystemMsg,
          }
          draft.draftMessage = {
            message: draftMessage,
            payload: {
              action_type: 'user_interface_modification',
              context: null,
              continue_editing: false,
            },
            selectedAgent: AiAgent.DESIGNER,
          }
        }
      }
    })
  ),
  // AGENT DEVELOPER
  on(
    CommandPaletteActions.generateNewBusinessRuleCommand,
    CtaActions.generateBusinessRuleCtaClick,
    (state: AiState) =>
      produce(state, (draft: AiState) => {
        if (canSelectAgent(state)) {
          const draftMessage = {
            message: 'Describe the business rule so I can help you generate it.',
            actions: {
              checkDocumentContext: state.permissions.canViewDocuments,
              imageUpload: false,
              processUpload: false,
            },
            type: MessageType.SystemMsg,
          }
          draft.draftMessage = {
            message: draftMessage,
            payload: {
              action_type: 'business_rule',
              context: null,
              continue_editing: false,
            },
            selectedAgent: AiAgent.DEVELOPER,
          }
        }
      })
  ),
  on(
    NodeMenuActions.generateUiNodeMenuActionTransitionEnd,
    (state: AiState, { userTaskNavigationAreaId }) =>
      produce(state, (draft: AiState) => {
        if (!draft.zenMode) {
          if (canSelectAgent(state)) {
            draft.zenMode = false
            draft.selectedDesignerUserTaskAreaId = userTaskNavigationAreaId
            openChatWithDocumentGenerationMsg(draft, {
              selectedAgent: AiAgent.DESIGNER,
            })
          }
        }
      })
  ),
  on(NodeMenuActions.generateBusinessRuleNodeMenuActionTransitionEnd, (state: AiState) =>
    produce(state, (draft: AiState) => {
      if (!draft.zenMode) {
        if (canSelectAgent(state)) {
          draft.zenMode = true
          draft.minimized = false
          draft.selectedAgent = AiAgent.DEVELOPER
          const draftMessage = {
            message: 'Describe the business rule so I can help you generate it.',
            actions: {
              checkDocumentContext: state.permissions.canViewDocuments,
              imageUpload: false,
              processUpload: false,
            },
            type: MessageType.SystemMsg,
          }
          draft.draftMessage = {
            message: draftMessage,
            payload: {
              action_type: 'business_rule',
              context: null,
              continue_editing: false,
            },
            selectedAgent: AiAgent.DEVELOPER,
          }
        }
      }
    })
  ),
  on(CtaActions.explainBusinessRuleCtaClick, (state: AiState) =>
    produce(state, (draft: AiState) => {
      if (canSelectAgent(state)) {
        openChat(draft, {
          selectedAgent: AiAgent.DEVELOPER,
          initialMessage: {
            message: 'Explain business rule',
            type: MessageType.UserMsg,
          },
          isChatConversationAllowed: false,
        })
      }
    })
  ),
  on(
    UiDesignerExpressionModalActions.generateClick,
    UiDesignerConfigPanelActions.expressionGenerateClick,
    (state: AiState) =>
      produce(state, (draft: AiState) => {
        if (canSelectAgent(state)) {
          const draftMessage = {
            message: 'Describe the business rule so I can help you generate it.',
            actions: {
              checkDocumentContext: state.permissions.canViewDocuments,
              imageUpload: false,
              processUpload: false,
            },
            type: MessageType.SystemMsg,
          }
          draft.draftMessage = {
            message: draftMessage,
            payload: {
              action_type: 'hide_expression',
              context: null,
              continue_editing: false,
            },
            selectedAgent: AiAgent.DEVELOPER,
          }
        }
      })
  ),
  // AGENT INTEGRATOR
  on(CommandPaletteActions.generateIntegrationSystemCommand, (state: AiState) =>
    produce(state, (draft: AiState) => {
      if (canSelectAgent(state)) {
        const draftMessage = {
          message: 'Specify the description of the system you want to generate.',
          actions: {
            checkDocumentContext: true,
            imageUpload: false,
            processUpload: false,
          },
          type: MessageType.SystemMsg,
        }
        draft.draftMessage = {
          message: draftMessage,
          payload: {
            action_type: 'integration-system',
            context: null,
            continue_editing: false,
          },
          selectedAgent: AiAgent.INTEGRATOR,
        }
      }
    })
  ),
  on(CommandPaletteActions.generateIntegrationWorkflowCommand, (state: AiState) =>
    produce(state, (draft: AiState) => {
      if (canSelectAgent(state)) {
        const draftMessage = {
          message: 'Specify the description of the integration workflow you want to generate.',
          actions: {
            checkDocumentContext: true,
            imageUpload: false,
            processUpload: false,
          },
          type: MessageType.SystemMsg,
        }
        draft.draftMessage = {
          message: draftMessage,
          payload: {
            action_type: 'integration-workflow',
            context: null,
            continue_editing: false,
          },
          selectedAgent: AiAgent.INTEGRATOR,
        }
      }
    })
  ),
  // AGENT ARCHITECT
  on(CommandPaletteActions.generateDataModelCommand, (state: AiState) =>
    produce(state, (draft: AiState) => {
      if (canSelectAgent(state)) {
        const draftMessage = {
          message: 'Specify the description of the data model you want to generate.',
          actions: {
            checkDocumentContext: false,
            imageUpload: false,
            processUpload: false,
          },
          type: MessageType.SystemMsg,
        }
        draft.draftMessage = {
          message: draftMessage,
          payload: {
            action_type: 'data_model',
            context: null,
            continue_editing: false,
          },
          selectedAgent: AiAgent.ARCHITECT,
        }
      }
    })
  ),
  on(CommandPaletteActions.generateImageCommand, (state: AiState) =>
    produce(state, (draft: AiState) => {
      if (canSelectAgent(state)) {
        const draftMessage = {
          message: 'Specify the description of the image you want to generate.',
          actions: {
            checkDocumentContext: false,
            imageUpload: false,
            processUpload: false,
          },
          type: MessageType.SystemMsg,
        }
        draft.draftMessage = {
          message: draftMessage,
          payload: {
            action_type: 'image',
            context: null,
            continue_editing: false,
          },
          selectedAgent: AiAgent.ARCHITECT,
        }
      }
    })
  ),
  on(CommandPaletteActions.generateNotificationTemplateCommand, (state: AiState) =>
    produce(state, (draft: AiState) => {
      if (canSelectAgent(state)) {
        const draftMessage = {
          message: 'Describe the enumeration content so I can generate items for it.',
          actions: {
            checkDocumentContext: false,
            imageUpload: false,
            processUpload: false,
          },
          type: MessageType.SystemMsg,
        }
        draft.draftMessage = {
          message: draftMessage,
          payload: {
            action_type: 'notification_template',
            context: null,
            continue_editing: false,
          },
          selectedAgent: AiAgent.ARCHITECT,
        }
      }
    })
  ),
  on(CommandPaletteActions.generateDocumentTemplateCommand, (state: AiState) =>
    produce(state, (draft: AiState) => {
      if (canSelectAgent(state)) {
        const draftMessage = {
          message: 'Specify what you want to generate as content for your document template.',
          actions: {
            checkDocumentContext: false,
            imageUpload: false,
            processUpload: false,
          },
          type: MessageType.SystemMsg,
        }
        draft.draftMessage = {
          message: draftMessage,
          payload: {
            action_type: 'document_template',
            context: null,
            continue_editing: false,
          },
          selectedAgent: AiAgent.ARCHITECT,
        }
      }
    })
  ),
  on(CommandPaletteActions.generateEnumerationCommand, (state: AiState) =>
    produce(state, (draft: AiState) => {
      if (canSelectAgent(state)) {
        const draftMessage = {
          message: 'Describe the enumeration content so I can generate items for it.',
          actions: {
            checkDocumentContext: false,
            imageUpload: false,
            processUpload: false,
          },
          type: MessageType.SystemMsg,
        }
        draft.draftMessage = {
          message: draftMessage,
          payload: {
            action_type: 'enumeration',
            context: null,
            continue_editing: false,
          },
          selectedAgent: AiAgent.ARCHITECT,
        }
      }
    })
  ),
  on(CommandPaletteActions.translateSubstitutionTagCommand, (state: AiState) =>
    produce(state, (draft: AiState) => {
      if (canSelectAgent(state)) {
        const draftMessage = {
          message: 'Describe the enumeration content so I can generate items for it.',
          actions: {
            checkDocumentContext: false,
            imageUpload: false,
            processUpload: false,
          },
          type: MessageType.SystemMsg,
        }
        draft.draftMessage = {
          message: draftMessage,
          payload: {
            action_type: 'substitution_tags',
            context: null,
            continue_editing: false,
          },
          selectedAgent: AiAgent.ARCHITECT,
        }
      }
    })
  ),
  on(
    CtaActions.processDesignerCtaClick,
    CtaActions.uiDesignerCtaClick,
    CtaActions.uiFlowDesignerCtaClick,
    CtaActions.generateBusinessRuleCtaClick,
    CtaActions.explainBusinessRuleCtaClick,
    SequenceMenuActions.generateProcess,
    NodeMenuActions.generateProcessNodeMenuAction,
    AiCodeEditorContextMenuActions.improveCodeOptionClick,
    AiCodeEditorContextMenuActions.addCommentOptionClick,
    AiDesignerActions.uiDesignerNodeAiClick,
    UiDesignerExpressionModalActions.generateClick,
    UiDesignerConfigPanelActions.expressionGenerateClick,
    setMinimizedFalse
  ),
  on(CtaActions.fixBusinessRuleWarningsCtaClick, (state: AiState) =>
    produce(state, (draft: AiState) => {
      if (canSelectAgent(state)) {
        draft.isAiInteractionAllowed = false
        openChat(draft, { selectedAgent: AiAgent.DEVELOPER })
      }
    })
  ),
  on(CtaActions.fixBusinessRuleErrorCtaClick, (state) =>
    produce(state, (draft) => {
      if (canSelectAgent(state)) {
        draft.isAiInteractionAllowed = false
        openChat(draft, { selectedAgent: AiAgent.DEVELOPER })
      }
    })
  ),
  on(CtaActions.fixBusinessRuleErrorCtaClick, (state) =>
    produce(state, (draft) => {
      if (canSelectAgent(state)) {
        draft.isAiInteractionAllowed = false
        openChat(draft, { selectedAgent: AiAgent.DEVELOPER })
      }
    })
  ),

  // Breadcrumbs
  on(BreadcrumbActions.backClick, (state: AiState) =>
    produce(state, (draft: AiState) => {
      closeChat(draft)
    })
  ),

  // TypeArea
  on(AiActions.typeAreaSubmit, (state: AiState, { text, files, agent }) =>
    produce(state, (draft: AiState) => {
      const message = {
        message: text,
        ...(files?.length ? { files } : {}),
        type: MessageType.UserMsg,
        commited: true,
      }
      if (state.zenMode) {
        draft.selectedAgent = agent
        draft.messages.push(message)
      } else {
        if (!state.selectedAgent) {
          openChat(draft, {
            selectedAgent: AiAgent.ASSISTANT,
            initialMessage: message,
          })
        } else {
          draft.messages.push(message)
        }
      }

      draft.uploadedFiles = []
    })
  ),
  on(
    AiAssistantActions.getAnswer,
    AiAnalystActions.extractProcessFromDocument,
    AiAnalystActions.generateProcessFromImages,
    AiAnalystActions.generateProcessFromBpmnFiles,
    AiAnalystActions.generateProcess,
    AiAnalystActions.convertBPMN,
    AiAnalystActions.insertInProcess,
    AiAnalystActions.generateProcessStream,

    AiAuditorActions.extractComplianceFromDocument,
    AiAuditorActions.checkProcess,
    AiAuditorActions.checkUI,
    AiAuditorActions.checkProcessStream,
    AiAuditorActions.checkUIStream,

    AiDesignerActions.extractUiFromDocument,
    AiDesignerActions.generateUiStream,
    AiDesignerActions.generateUiFromImagesStream,
    AiDesignerActions.generateUiTemplateStream,
    AiDesignerActions.generateUiTemplateFromImagesStream,
    AiDesignerActions.editUiTemplateStream,

    AiDeveloperActions.generateBusinessRule,
    AiDeveloperActions.generateReusableFunction,
    AiDeveloperActions.extractBusinessRuleFromDocument,
    AiDeveloperActions.explainBusinessRule,
    AiDeveloperActions.improveCode,
    AiDeveloperActions.addComment,
    AiDeveloperActions.fixBusinessRuleWarnings,
    AiDeveloperActions.fixBusinessRuleError,
    AiDeveloperActions.generateExpression,
    AiDeveloperActions.generateGatewayCondition,

    AiActions.initialMessage,
    AiArchitectActions.generateImage,
    AiArchitectActions.generateEnumeration,
    AiArchitectActions.generateDataModel,
    AiArchitectActions.generateDocTemplate,
    AiArchitectActions.generateNotTemplate,
    AiArchitectActions.translateSubstitutionTags,

    AiDocumentsActions.uploadDocuments,
    (state: AiState) =>
      produce(state, (draft: AiState) => {
        draft.pendingAiResponse = true
      })
  ),
  on(AiDeveloperActions.fixBusinessRuleWarnings, (state: AiState) =>
    produce(state, (draft: AiState) => {
      draft.pendingFixWarningsResponse = true
    })
  ),
  on(AiDeveloperActions.fixBusinessRuleError, (state) =>
    produce(state, (draft) => {
      draft.pendingFixErrorResponse = true
    })
  ),
  on(
    AiAnalystActions.keepProcessDraft,
    AiDesignerActions.keepUiDraft,
    AiDeveloperActions.keepDraft,
    (state: AiState) =>
      produce(state, (draft: AiState) => {
        draft.pendingKeepDraftSave = true
      })
  ),

  // Code editor actions
  on(AiCodeEditorContextMenuActions.improveCodeOptionClick, (state) =>
    produce(state, (draft) => {
      openChat(draft, {
        selectedAgent: AiAgent.DEVELOPER,
        initialMessage: {
          message: 'Improve the selected code',
          type: MessageType.UserMsg,
        },
        isChatConversationAllowed: false,
      })
    })
  ),
  on(AiCodeEditorContextMenuActions.addCommentOptionClick, (state) =>
    produce(state, (draft) => {
      openChat(draft, {
        selectedAgent: AiAgent.DEVELOPER,
        initialMessage: {
          message: 'Generate comments for this code',
          type: MessageType.UserMsg,
        },
        isChatConversationAllowed: false,
      })
    })
  ),

  // Code editor actions
  on(AiCodeEditorContextMenuActions.improveCodeOptionClick, (state) =>
    produce(state, (draft) => {
      openChat(draft, {
        selectedAgent: AiAgent.DEVELOPER,
        initialMessage: {
          message: 'Improve the selected code',
          type: MessageType.UserMsg,
        },
        isChatConversationAllowed: false,
      })
    })
  ),
  on(AiCodeEditorContextMenuActions.addCommentOptionClick, (state) =>
    produce(state, (draft) => {
      openChat(draft, {
        selectedAgent: AiAgent.DEVELOPER,
        initialMessage: {
          message: 'Generate comments for this code',
          type: MessageType.UserMsg,
        },
        isChatConversationAllowed: false,
      })
    })
  ),

  // Developer
  on(AiDeveloperActions.generateBusinessRuleSuccess, onGenerateBusinessRuleSuccess),
  on(AiDeveloperActions.generateBusinessRuleFailure, onAgentFailure),
  on(AiDeveloperActions.explainBusinessRuleSuccess, onExplainBusinessRuleSuccess),
  on(AiDeveloperActions.improveCode, AiDeveloperActions.addComment, (state, { selection }) =>
    produce(state, (draft) => {
      draft.codeSelectionContext = selection
    })
  ),
  on(AiDeveloperActions.improveCodeSuccess, onImproveCodeSuccess),
  on(AiDeveloperActions.addCommentSuccess, onAddCommentSuccess),
  on(AiDeveloperActions.fixBusinessRuleWarningsSuccess, onFixBusinessRuleWarningsSuccess),
  on(AiDeveloperActions.fixBusinessRuleErrorSuccess, onFixBusinessRuleErrorSuccess),
  on(AiDeveloperActions.generateExpressionFailure, onAgentFailure),
  on(AiDeveloperActions.generateExpressionSuccess, onGenerateExpressionSuccess),
  on(AiDeveloperActions.generateGatewayConditionSuccess, onGenerateGatewayConditionSuccess),
  on(AiDeveloperActions.generateGatewayConditionFailure, onAgentFailure),
  on(
    AiDeveloperActions.keepBusinessRuleDraftSuccess,
    AiDeveloperActions.keepExpressionDraftSuccess,
    AiDeveloperActions.keepFixWarningsBusinessRuleDraftSuccess,
    AiDeveloperActions.keepImprovedCodeDraftSuccess,
    AiDeveloperActions.keepAddCommentDraftSuccess,
    AiDeveloperActions.keepGatewayConditionDraftSuccess,
    AiDeveloperActions.keepReusableFunctionDraftSuccess,
    (state: AiState) =>
      produce(state, (draft: AiState) => {
        if (state.zenMode) {
          draft.selectedPlan?.actions.forEach((action) => {
            action.steps = action.steps.map((step) =>
              step.step_number === draft.currentPlanStep?.step_number
                ? {
                    ...step,
                    step_status: 'completed',
                    executionOutput: draft.generatedBusinessRule,
                  }
                : step
            )
          })
          draft.currentPlanStep = null
          draft.generatedBusinessRule = null
          draft.generatedExpression = null
          draft.generatedGatewayCondition = null
          draft.generatedFixWarningsBusinessRule = null
          draft.generatedFixErrorBusinessRule = null
          draft.generatedImprovedCode = null
          draft.generatedAddComment = null
          draft.generatedTestData = null
          draft.messages.push({
            message: 'Keep the generated business rule.',
            type: MessageType.UserMsg,
            commited: true,
          })
        } else {
          draft.minimized = true
          closeChat(draft)
        }
      })
  ),
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  on(AiActions.createSendMessageActionSuccess, (state: AiState, { action }) =>
    produce(state, (draft: AiState) => {
      if (state.zenMode) {
        draft.selectedPlan?.actions.forEach((action) => {
          action.steps = action.steps.map((step) =>
            step.step_number === draft.currentPlanStep?.step_number
              ? {
                  ...step,
                  step_status: 'completed',
                  executionOutput: null,
                }
              : step
          )
        })
        draft.currentPlanStep = null
        draft.pendingRouteToDeveloper = null
        draft.messages.push({
          message: 'Send message action created successfully.',
          type: MessageType.AiMsg,
          commited: true,
        })
      }
    })
  ),
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  on(AiActions.createReceiveMessageActionSuccess, (state: AiState, { action }) =>
    produce(state, (draft: AiState) => {
      if (state.zenMode) {
        draft.selectedPlan?.actions.forEach((action) => {
          action.steps = action.steps.map((step) =>
            step.step_number === draft.currentPlanStep?.step_number
              ? {
                  ...step,
                  step_status: 'completed',
                  executionOutput: null,
                }
              : step
          )
        })
        draft.currentPlanStep = null
        draft.pendingRouteToDeveloper = null
        draft.messages.push({
          message: 'Receive message action created successfully.',
          type: MessageType.AiMsg,
          commited: true,
        })
      }
    })
  ),
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  on(AiActions.createBusinessRuleUploadActionSuccess, (state: AiState, { action }) =>
    produce(state, (draft: AiState) => {
      if (state.zenMode) {
        draft.selectedPlan?.actions.forEach((action) => {
          action.steps = action.steps.map((step) =>
            step.step_number === draft.currentPlanStep?.step_number
              ? {
                  ...step,
                  step_status: 'completed',
                  executionOutput: null,
                }
              : step
          )
        })
        draft.currentPlanStep = null
        draft.pendingRouteToDeveloper = null
        draft.messages.push({
          message: 'Business rule upload action created successfully.',
          type: MessageType.AiMsg,
          commited: true,
        })
      }
    })
  ),

  on(
    AiDeveloperActions.discardBusinessRuleDraft,
    AiDeveloperActions.discardExpressionDraft,
    AiDeveloperActions.discardFixWarningsBusinessRuleDraft,
    AiDeveloperActions.discardFixErrorBusinessRuleDraft,
    AiDeveloperActions.discardImprovedCodeDraft,
    AiDeveloperActions.discardAddCommentDraft,
    AiDeveloperActions.discardGatewayConditionDraft,
    (state: AiState) =>
      produce(state, (draft: AiState) => {
        if (state.zenMode) {
          draft.selectedPlan?.actions.forEach((action) => {
            action.steps = action.steps.map((step) =>
              step.step_number === draft.currentPlanStep?.step_number
                ? { ...step, step_status: 'default', executionOutput: null }
                : step
            )
          })
          draft.currentPlanStep = null
          draft.generatedBusinessRule = null
          draft.generatedExpression = null
          draft.generatedGatewayCondition = null
          draft.generatedFixWarningsBusinessRule = null
          draft.generatedFixErrorBusinessRule = null
          draft.generatedImprovedCode = null
          draft.generatedAddComment = null
          draft.generatedTestData = null
          draft.messages.push({
            message: 'Discard the generated business rule.',
            type: MessageType.UserMsg,
            commited: true,
          })
        } else {
          draft.minimized = true
          closeChat(draft)
        }
      })
  ),

  on(AiAuditorActions.discardAuditDraft, (state: AiState) =>
    produce(state, (draft: AiState) => {
      draft.hasAudit = false
      draft.generatedAiRecommendation = null
    })
  ),

  // Architect
  on(AiActions.initialMessageSuccess, (state: AiState, { response, message, selectedAgent }) =>
    produce(state, (draft: AiState) => {
      const welcomeMessage = {
        ...message,
        message: response.answer.message,
        prompts:
          response.answer.prompts.map((prompt: any) => ({
            action: prompt.value,
            description: prompt.value,
            handled_by: selectedAgent,
          })) || [],
      }
      draft.initialMessage = welcomeMessage

      // If user already submitted messages while the welcome API was in flight,
      // prepend the welcome message instead of resetting the entire messages array
      if (draft.messages.length > 0) {
        draft.messages.unshift(welcomeMessage)
        if (!draft.selectedAgent) {
          draft.selectedAgent = selectedAgent
        }
      } else {
        openChat(draft, {
          selectedAgent,
          initialMessage: welcomeMessage,
        })
      }
    })
  ),
  on(AiArchitectActions.generateNotTemplateSuccess, (state: AiState, { response }) =>
    produce(state, (draft: AiState) => {
      if (response.chat) {
        draft.messages.push({
          message: response.chat,
          type: MessageType.AiMsg,
          commited: true,
        })
      } else {
        draft.generatedNotification = response.answer
        draft.messages.push({
          message:
            'The notification template with subject **' +
            response.answer.subject +
            '** has been generated. You can tweak the results by telling me what to change or edit the preview directly before saving.',
          context: {
            ...response,
            answer: JSON.stringify(response.answer),
          },
          type: MessageType.AiMsg,
          commited: true,
        })
      }
    })
  ),
  on(AiArchitectActions.generateDocTemplateSuccess, (state: AiState, { response }) =>
    produce(state, (draft: AiState) => {
      if (response.chat) {
        draft.messages.push({
          message: response.chat,
          type: MessageType.AiMsg,
          commited: true,
        })
      } else {
        draft.generatedNotification = response.answer
        draft.messages.push({
          message:
            'The document template with subject **' +
            response.answer.subject +
            '** has been generated. You can tweak the results by telling me what to change or edit the preview directly before saving.',
          context: {
            ...response,
            answer: JSON.stringify(response.answer),
          },
          type: MessageType.AiMsg,
          commited: true,
        })
      }
    })
  ),

  on(AiArchitectActions.generateDataModelSuccess, (state: AiState, { response }) =>
    produce(state, (draft: AiState) => {
      if (response.chat) {
        draft.messages.push({
          message: response.chat,
          type: MessageType.AiMsg,
          commited: true,
        })
      } else {
        draft.messages.push({
          message:
            'Data model was updated. You can tweak the results by telling me what to change or edit the preview directly before saving.',
          context: {
            ...response,
            answer: JSON.stringify(response.answer),
          },
          type: MessageType.AiMsg,
          commited: true,
        })
      }
    })
  ),
  on(AiArchitectActions.generateImageSuccess, (state: AiState, { response }) =>
    produce(state, (draft: AiState) => {
      if (response.chat) {
        draft.messages.push({
          message: response.chat,
          type: MessageType.AiMsg,
          commited: true,
        })
      } else {
        draft.generatedImage =
          response.answer.image_type === 'SVG'
            ? `data:image/svg+xml;charset=utf-8,${encodeURIComponent(response.answer.image_content as string)}`
            : `data:image/png;base64,${response.answer.image_content}`
        draft.messages.push({
          message:
            'Image was generated successfully. \n This is the description of the image: \n\r **' +
            response.answer.image_description +
            '**',
          context: {
            ...response,
            answer: JSON.stringify(response.answer),
          },
          generatedImage: draft.generatedImage,
          type: MessageType.AiMsg,
          commited: true,
        })
      }
    })
  ),
  on(AiArchitectActions.generateEnumerationSuccess, (state: AiState, { response }) =>
    produce(state, (draft: AiState) => {
      if (response.chat) {
        draft.messages.push({
          message: response.chat,
          type: MessageType.AiMsg,
          commited: true,
        })
      } else {
        draft.generatedEnumeration = response.answer
        draft.messages.push({
          message:
            'Modifications on enumeration has been generated. You can tweak the results by telling me what to change or edit the preview directly before saving.',
          context: {
            ...response,
            answer: JSON.stringify(response.answer),
          },
          type: MessageType.AiMsg,
          commited: true,
        })
      }
    })
  ),
  on(AiArchitectActions.translateSubstTagsSuccess, (state: AiState, { response }) =>
    produce(state, (draft: AiState) => {
      if (response.chat) {
        draft.messages.push({
          message: response.chat,
          type: MessageType.AiMsg,
          commited: true,
        })
      } else {
        draft.generatedEnumeration = response.answer

        draft.messages.push({
          message:
            'Modifications on substitution tags has been generated. You can tweak the results by telling me what to change or edit the preview directly before saving.',
          context: {
            ...response,
            answer: JSON.stringify(response.answer),
          },
          type: MessageType.AiMsg,
          commited: true,
        })
      }
    })
  ),
  on(AiArchitectActions.discardDraft, (state: AiState) =>
    produce(state, (draft: AiState) => {
      if (state.zenMode) {
        draft.messages.push({
          message: 'Discard the generated data model.',
          type: MessageType.UserMsg,
          commited: true,
        })
        draft.selectedPlan?.actions.forEach((action) => {
          action.steps = action.steps.map((step) =>
            step.step_number === draft.currentPlanStep?.step_number
              ? { ...step, step_status: 'default' }
              : step
          )
        })
        draft.currentPlanStep = null
        draft.generatedDataModel = null
      } else {
        draft.minimized = true
        closeChat(draft)
      }
    })
  ),
  // Designer
  on(AiDesignerActions.generateUiSuccess, onGenerateUiSuccess),
  on(AiDesignerActions.generateUiFailure, onAgentFailure),
  on(
    AiDesignerActions.mapTemplateConfigComponentsToDataModel,
    onMapTemplateConfigComponentsToDataModel
  ),
  on(AiIntegratorActions.keepAgentDraftSuccess, onKeepAgentDraftSuccess),
  on(AiIntegratorActions.getAnswerEventStream, onIntegratorGetAnswerEventStream),
  on(AiIntegratorActions.keepDatasourceDraftSuccess, onKeepDatasourceDraftSuccess),
  on(AiIntegratorActions.keepEditedDatasourceDraftSuccess, onKeepDatasourceDraftSuccess),
  on(AiIntegratorActions.discardDatasourceDraft, onDiscardDatasourceDraft),
  on(AiDesignerActions.keepTemplateConfigDraftSuccess, onKeepTemplateConfigDraftSuccess),
  on(AiDesignerActions.updateDataModelOnLastMessage, onUpdateDataModelOnLastMessage),
  on(AiDesignerActions.discardTemplateConfigDraft, onDiscardUIDesignerDraft),
  on(AiDesignerActions.discardDataModelDraftSuccess, onDiscardUIDesignerDraft),
  // Analyst
  on(
    AiAnalystActions.generateProcessSuccess,
    AiAnalystActions.convertBPMNSuccess,
    onGenerateProcessSuccess
  ),
  on(AiAnalystActions.generateProcessFailure, onAgentFailure),
  on(AiAnalystActions.discardProcessDraftSuccess, (state: AiState) =>
    produce(state, (draft: AiState) => {
      if (state.zenMode) {
        draft.messages.push({
          message: 'Discard the generated process.',
          type: MessageType.UserMsg,
          commited: true,
        })
        draft.selectedPlan?.actions.forEach((action) => {
          action.steps = action.steps.map((step) =>
            step.step_number === draft.currentPlanStep?.step_number
              ? { ...step, step_status: 'default' }
              : step
          )
        })
        draft.currentPlanStep = null
        draft.generatedProcess = null
        draft.generatedProcessEntities = null
        draft.temporaryProcess = null
        draft.temporaryProcessEntities = null
      } else {
        draft.minimized = true
        newChatSession(draft)
        closeChat(draft)
      }
    })
  ),
  on(AiCoordinatorActions.createPlanSuccess, (state: AiState, { plan }) =>
    produce(state, (draft: AiState) => {
      draft.selectedPlan = { ...plan.plan, id: plan.id }
    })
  ),
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  on(AiAnalystActions.keepProcessDraftSuccess, (state: AiState, { response }) =>
    produce(state, (draft: AiState) => {
      draft.messages.push({
        message: 'Keep the generated process.',
        type: MessageType.UserMsg,
        commited: true,
      })

      const currentProcess = AiAnalystUtils.getInitialProcessContext()
      draft.selectedPlan?.actions.forEach((action) => {
        if (action.agent === AiAgent.ANALYST) {
          action.steps = action.steps.map((step) =>
            step.step_number === draft.currentPlanStep?.step_number
              ? { ...step, step_status: 'completed', executionOutput: currentProcess }
              : step
          )
        }
      })

      draft.generatedProcess = null
      draft.generatedProcessEntities = null
      draft.temporaryProcess = null
      draft.temporaryProcessEntities = null
    })
  ),
  on(AiDeveloperActions.keepBusinessRuleDraftSuccess, (state: AiState) =>
    produce(state, (draft: AiState) => {
      draft.selectedPlan?.actions.forEach((action) => {
        action.steps = action.steps.map((step) =>
          step.step_number === draft.currentPlanStep?.step_number
            ? { ...step, step_status: 'completed', executionOutput: draft.generatedBusinessRule }
            : step
        )
      })
      draft.generatedTestData = null
      draft.generatedBusinessRule = null
    })
  ),
  on(AiDeveloperActions.keepGatewayConditionDraftSuccess, (state: AiState) =>
    produce(state, (draft: AiState) => {
      draft.selectedPlan?.actions.forEach((action) => {
        action.steps = action.steps.map((step) =>
          step.step_number === draft.currentPlanStep?.step_number
            ? {
                ...step,
                step_status: 'completed',
                executionOutput: draft.generatedGatewayCondition,
              }
            : step
        )
      })
      draft.generatedGatewayCondition = null
    })
  ),
  on(AiAnalystActions.keepProcessDraftFailure, (state: AiState) =>
    produce(state, (draft: AiState) => {
      draft.selectedPlan?.actions.forEach((action) => {
        if (action.agent === AiAgent.ANALYST) {
          action.steps = action.steps.map((step) =>
            step.step_number === draft.currentPlanStep?.step_number
              ? { ...step, step_status: 'default', executionOutput: null }
              : step
          )
        }
      })
    })
  ),
  // Coordinator
  on(AiCoordinatorActions.getAnswerEventStream, onCoordinatorEventStream),
  on(AiCoordinatorActions.keepAgentDraft, (state: AiState, { response }) =>
    produce(state, (draft: AiState) => {
      draft.selectedPlan?.actions.forEach((action) => {
        action.steps = action.steps.map((step) =>
          step.step_number === draft.currentPlanStep?.step_number
            ? { ...step, step_status: 'completed', executionOutput: response }
            : step
        )
      })
      draft.currentPlanStep = null
      draft.generatedAgentWorkflow = null
    })
  ),

  on(AiCoordinatorActions.keepAgentDraftSuccess, (state: AiState, { response }) =>
    produce(state, (draft: AiState) => {
      draft.selectedPlan?.actions.forEach((action) => {
        action.steps = action.steps.map((step) =>
          step.step_number === draft.currentPlanStep?.step_number
            ? {
                ...step,
                step_status: 'completed',
                executionOutput: response,
                step_destination: step.step_destination
                  ? { ...step.step_destination, id: response?.id ?? step.step_destination.id }
                  : step.step_destination,
              }
            : step
        )
      })
      draft.currentPlanStep = null
    })
  ),
  on(AiCoordinatorActions.getIntentSuccess, (state: AiState) =>
    produce(state, (draft: AiState) => {
      draft.generatingPlan = false
    })
  ),
  on(AiCoordinatorActions.generateActionSuccess, onGeneratedActionHandler),
  on(AiCoordinatorActions.generatePromptForAgentSuccess, onGeneratedPromptForAgentSuccess),
  on(AiDesignerActions.getAnswerEventStream, onDesignerEventStream),
  on(AiDesignerActions.keepDataModelDraftSuccess, (state: AiState) =>
    produce(state, (draft: AiState) => {
      draft.generatedUIDataModelChanges = null
    })
  ),
  on(AiAnalystActions.getAnswerEventStream, onAnalystEventStream),
  on(AiArchitectActions.getAnswerEventStream, onArchitectEventStream),
  on(AiDeveloperActions.getAnswerEventStream, onDeveloperEventStream),
  on(AiDeveloperActions.getCustomComponentAnswerEventStream, onDeveloperEventStream),
  on(AiDeveloperActions.setCustomComponentContext, (state: AiState, { context }) =>
    produce(state, (draft: AiState) => {
      draft.customComponentContext = context
    })
  ),
  on(AiDeveloperActions.keepCustomComponentDraftSuccess, (state: AiState) =>
    produce(state, (draft: AiState) => {
      draft.generatedCustomComponent = null
      draft.backgroundAiResponse = false
      draft.isAiInteractionAllowed = true
      if (draft.messages.length > 0) {
        draft.messages[draft.messages.length - 1].commited = true
      }
    })
  ),
  on(AiDeveloperActions.discardCustomComponentDraft, (state: AiState) =>
    produce(state, (draft: AiState) => {
      draft.generatedCustomComponent = null
      draft.backgroundAiResponse = false
      draft.isAiInteractionAllowed = true
    })
  ),
  on(AiDeveloperActions.setReusableFunctionContext, (state: AiState, { context }) =>
    produce(state, (draft: AiState) => {
      draft.reusableFunctionContext = context
    })
  ),
  on(AiActions.clearGeneratedActions, (state: AiState) =>
    produce(state, (draft: AiState) => {
      draft.generatedActions = []
      draft.pendingRouteToCoordinator = null
    })
  ),
  on(ProcessDataModelActions.keepTemporaryDataModelSuccess, (state: AiState) =>
    produce(state, (draft: AiState) => {
      draft.selectedPlan?.actions.forEach((action) => {
        action.steps = action.steps.map((step) =>
          step.step_number === draft.currentPlanStep?.step_number
            ? {
                ...step,
                step_status: 'completed',
                executionOutput: draft.generatedDataModel,
              }
            : step
        )
      })
      draft.currentPlanStep = null
      draft.generatedDataModel = null
    })
  ),
  on(AiActions.updateStep, onUpdateStep),
  on(AiActions.invokeStep, onInvokeStep),
  on(AiActions.deletePlan, onDeletePlan),
  on(AiActions.yesAction, onYesAction),
  on(AiActions.noAction, onNoAction),
  on(AiActions.selectInterruptOption, onSelectInterruptOption),
  on(AiActions.refineList, onRefineList),
  on(AiActions.automateProcess, onAutomateProcess),

  // Assistant
  on(AiAssistantActions.getAnswerEventStream, onAssistantEventStream),
  on(AiAssistantActions.getAnswerEventStreamOld, onAssistantEventStreamOld),
  on(
    AiActions.getAnswerStreamCompleted,
    AiActions.getAnswerFailure,
    AiActions.cancelPendingRequest,
    (state: AiState) =>
      produce(state, (draft: AiState) => {
        draft.streamingAiResponse = false
        draft.backgroundAiResponse = false
        draft.isAiInteractionAllowed = true
        draft.generatingUi = false
        draft.generatingPlan = false
      })
  ),

  on(AiActions.setScreenContext, (state: AiState, { screenContext }) =>
    produce(state, (draft: AiState) => {
      draft.screen_context = screenContext
    })
  ),

  on(AiActions.setDesignerVersion, (state: AiState, { version }) =>
    produce(state, (draft: AiState) => {
      draft.designerVersion = version
    })
  ),

  on(
    AiAnalystActions.generateProcessSuccess,
    AiAnalystActions.generateProcessFailure,
    AiAuditorActions.extractComplianceFromDocumentSuccess,
    AiAuditorActions.extractComplianceFromDocumentFailure,
    AiAuditorActions.checkUI,
    AiAuditorActions.checkUISuccess,
    AiAuditorActions.checkUIFailure,
    AiAuditorActions.checkProcess,
    AiAuditorActions.checkProcessSuccess,
    AiAuditorActions.checkProcessFailure,
    AiAuditorActions.streamCompleted,

    AiAnalystActions.convertBPMNSuccess,
    AiAnalystActions.convertBPMNFailure,

    AiDesignerActions.generateUiSuccess,
    AiDesignerActions.generateUiFailure,

    AiDeveloperActions.generateBusinessRuleSuccess,
    AiDeveloperActions.generateBusinessRuleFailure,
    AiDeveloperActions.explainBusinessRuleSuccess,
    AiDeveloperActions.explainBusinessRuleFailure,
    AiDeveloperActions.improveCodeSuccess,
    AiDeveloperActions.improveCodeFailure,
    AiDeveloperActions.addCommentSuccess,
    AiDeveloperActions.addCommentFailure,
    AiDeveloperActions.fixBusinessRuleWarningsSuccess,
    AiDeveloperActions.fixBusinessRuleWarningsFailure,
    AiDeveloperActions.fixBusinessRuleErrorSuccess,
    AiDeveloperActions.fixBusinessRuleErrorFailure,
    AiDeveloperActions.generateExpressionSuccess,
    AiDeveloperActions.generateExpressionFailure,
    AiDeveloperActions.generateGatewayConditionSuccess,
    AiDeveloperActions.generateGatewayConditionFailure,
    AiArchitectActions.generateImageSuccess,
    AiArchitectActions.generateImageFailure,
    AiArchitectActions.generateEnumerationSuccess,
    AiArchitectActions.generateEnumerationFailure,
    AiArchitectActions.generateDataModelSuccess,
    AiArchitectActions.generateDataModelFailure,
    AiArchitectActions.generateDocTemplateSuccess,
    AiArchitectActions.generateDocTemplateFailure,
    AiArchitectActions.generateNotTemplateSuccess,
    AiArchitectActions.generateNotTemplateFailure,
    AiArchitectActions.translateSubstTagsFailure,
    AiArchitectActions.translateSubstTagsSuccess,
    AiActions.initialMessageSuccess,
    AiActions.initialMessageFailure,

    AiDocumentsActions.uploadDocumentsSuccess,
    AiDocumentsActions.uploadDocumentsFailure,
    AiActions.cancelPendingRequest,
    AiActions.getAnswerStreamCompleted,
    AiActions.getAnswerFailure,

    (state: AiState) =>
      produce(state, (draft: AiState) => {
        draft.pendingAiResponse = false
        draft.pendingDocumentsLoading = false
        draft.currentAITaskMessage = null
        draft.documentUploadMessage = null
      })
  ),
  on(
    AiDeveloperActions.fixBusinessRuleWarningsSuccess,
    AiDeveloperActions.fixBusinessRuleWarningsFailure,
    (state: AiState) =>
      produce(state, (draft: AiState) => {
        draft.pendingFixWarningsResponse = false
      })
  ),
  on(
    AiDeveloperActions.fixBusinessRuleErrorFailure,
    AiDeveloperActions.fixBusinessRuleErrorSuccess,
    AiActions.cancelPendingRequest,
    (state) =>
      produce(state, (draft) => {
        draft.pendingFixErrorResponse = false
      })
  ),
  on(
    AiDeveloperActions.fixBusinessRuleErrorFailure,
    AiDeveloperActions.fixBusinessRuleErrorSuccess,
    AiActions.cancelPendingRequest,
    (state) =>
      produce(state, (draft) => {
        draft.pendingFixErrorResponse = false
      })
  ),
  on(
    AiAnalystActions.keepProcessDraftSuccess,
    AiDesignerActions.keepTemplateConfigDraftSuccess,
    AiDesignerActions.saveTemplateConfigDraftFailure,
    AiDesignerActions.saveDataModelDraftFailure,
    AiDesignerActions.keepDataModelDraftSuccess,
    AiDesignerActions.linkTemplateConfigKeysFailure,
    AiDeveloperActions.keepBusinessRuleDraftSuccess,
    AiDeveloperActions.keepExpressionDraftSuccess,
    AiDeveloperActions.keepFixWarningsBusinessRuleDraftSuccess,
    AiDeveloperActions.keepImprovedCodeDraftSuccess,
    AiDeveloperActions.keepAddCommentDraftSuccess,
    AiDeveloperActions.keepGatewayConditionDraftSuccess,
    AiDeveloperActions.keepExpressionDraftFailure,
    (state: AiState) =>
      produce(state, (draft: AiState) => {
        draft.pendingKeepDraftSave = false
      })
  ),
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  on(AiActions.getAnswerFailure, (state: AiState, { statusCode, errorMsg }) =>
    produce(state, (draft: AiState) => {
      draft.messages.push({
        message: `Something went wrong and I can't help you with this right now. Anything else I can help you with?`,
        type: MessageType.SystemMsg,
      })
    })
  ),
  on(AiActions.cancelPendingRequest, (state: AiState) =>
    produce(state, (draft: AiState) => {
      draft.uploadingDocuments = []
    })
  ),

  on(AiActions.businessRuleContextReset, (state, { businessRuleContext }) =>
    produce(state, (draft) => {
      draft.businessRuleContext = businessRuleContext
    })
  ),

  // Keyboard
  on(KeyboardActions.escape, (state, { isBreadcrumbsBackDisabled }) =>
    produce(state, (draft: AiState) => {
      const canCloseChat = !state.pendingAiResponse && !hasEdits(draft)
      if (!isBreadcrumbsBackDisabled) {
        if (state.selectedAgent && canCloseChat) {
          // closeChat(draft)
        } else {
          draft.minimized = true
        }
      }
    })
  ),

  on(KeyboardActions.searchShortcut, (state: AiState) =>
    produce(state, (draft: AiState) => {
      if (state.zenMode) {
        // draft.minimized = true
      } else {
        draft.minimized = false
        closeChat(draft)
      }
    })
  ),

  on(KeyboardActions.toggleZenMode, (state) =>
    produce(state, (draft: AiState) => {
      draft.zenMode = !draft.zenMode
      draft.selectedAgent = draft.zenMode ? state.selectedAgent : null

      // if (draft.zenMode) {
      //   draft.sessionId = uuidV4()
      //   draft.messages = []
      // } else {
      //   draft.sessionId = null
      //   draft.messages = []
      // }
    })
  ),
  on(AiActions.openZenMode, (state: AiState, { agent, agentActions, pendingPlanMessage }) =>
    produce(state, (draft: AiState) => {
      draft.zenMode = true
      draft.selectedAgent = agent
      draft.agentActions = agentActions
      draft.sessionId = uuidV4()

      if (pendingPlanMessage) {
        draft.messages.push({
          message: pendingPlanMessage,
          type: MessageType.UserMsg,
        })
        draft.pendingAiResponse = true
      }
    })
  ),

  on(AiActions.canvasTransform, (state: AiState, { generatedProcessPreviewHeader }) =>
    produce(state, (draft: AiState) => {
      draft.generatedProcessPreviewHeader = generatedProcessPreviewHeader
    })
  ),

  on(AiActions.sendFeedback, (state: AiState, { feedback }) =>
    produce(state, (draft: AiState) => {
      // Most agent handlers null `message.context` after render — so matching
      // by context.request_id only finds Assistant/Coordinator messages.
      // Fall back to the most recent unrated AI message, which is what the
      // user clicked thumbs on in every practical case.
      let messageDraft = draft.messages.find(
        (message) => message.context?.request_id === feedback.requestId
      )
      if (!messageDraft) {
        for (let i = draft.messages.length - 1; i >= 0; i--) {
          const m = draft.messages[i]
          if (m.type === MessageType.AiMsg && !m.feedback) {
            messageDraft = m
            break
          }
        }
      }
      if (messageDraft) {
        messageDraft.feedback = feedback.feedbackType
      }
    })
  ),

  on(AiActions.closeChatConfirmed, (state: AiState) =>
    produce(state, (draft: AiState) => {
      closeChat(draft)
    })
  ),

  on(AiActions.cancelPendingRequestClick, (state: AiState) =>
    produce(state, (draft: AiState) => {
      if (draft.zenMode) {
        draft.selectedPlan?.actions.forEach((action) => {
          action.steps = action.steps.map((step) =>
            step.step_number === draft.currentPlanStep?.step_number
              ? { ...step, step_status: 'default', executionOutput: null }
              : step
          )
        })
        draft.currentPlanStep = null
      }

      draft.messages.push({
        message: `You've canceled your request`,
        type: MessageType.SystemMsg,
      })
    })
  ),

  on(AiActions.startNewSession, (state: AiState) =>
    produce(state, (draft) => {
      const {
        isFileUploadAllowed,
        startedFromSequence,
        selectedDesignerUserTaskAreaId,
        selectedDesignerTemplateConfigId,
      } = draft
      if (last(draft.messages)?.actions?.startNewSession) {
        last(draft.messages).actions.startNewSession = false
      }

      newChatSession(draft)
      draft.isFileUploadAllowed = isFileUploadAllowed
      draft.startedFromSequence = startedFromSequence
      // TODO: BOGDAN R -> USE THIS WHEN OPEN CHAT WINDOW FOR DESIGNER
      draft.selectedDesignerUserTaskAreaId = selectedDesignerUserTaskAreaId
      draft.selectedDesignerTemplateConfigId = selectedDesignerTemplateConfigId
      draft.waitingForHumanApproval = false
      draft.pendingRouteToCoordinator = null
      draft.currentAITaskMessage = null
      draft.pendingRouteToAssistant = null
      draft.pendingRouteToAnalyst = null
      draft.pendingRouteToDeveloper = null
      draft.pendingRouteToDesigner = null
      draft.pendingRouteToArchitect = null
      draft.pendingRouteToIntegrator = null
      draft.generatedIntent = null
      draft.messages = []

      draft.messages.push({ message: `Started a new session`, type: MessageType.SystemMsg })
    })
  ),

  // Errors
  on(
    AiActions.getAnswerFailure,
    AiAnalystActions.generateProcessFailure,
    AiAnalystActions.keepProcessDraftFailure,
    AiDesignerActions.generateUiFailure,
    AiDesignerActions.saveTemplateConfigDraftFailure,
    AiDesignerActions.saveDataModelDraftFailure,
    AiDesignerActions.linkTemplateConfigKeysFailure,
    AiDeveloperActions.generateBusinessRuleFailure,
    AiDeveloperActions.explainBusinessRuleFailure,
    AiDeveloperActions.improveCodeFailure,
    AiDeveloperActions.addCommentFailure,
    AiDeveloperActions.fixBusinessRuleWarningsFailure,
    AiDeveloperActions.fixBusinessRuleErrorFailure,
    AiDeveloperActions.generateExpressionFailure,
    AiDocumentsActions.uploadDocumentsFailure,
    (state, { statusCode, errorMsg }) =>
      produce(state, (draft) => {
        draft.messages.push(generateChatErrorMessage(errorMsg, statusCode))
        draft.pendingRouteToCoordinator = null
        draft.pendingRouteToAssistant = null
        draft.pendingRouteToAnalyst = null
        draft.pendingRouteToDeveloper = null
        draft.pendingRouteToDesigner = null
        draft.pendingRouteToArchitect = null
        draft.pendingRouteToIntegrator = null
        draft.pendingRouteToIntegrator = null
      })
  ),

  // ─── UI Flow Designer ──────────────────────────────────────────────────────
  on(AiDesignerActions.generateUiFlowStreamSuccess, (state: AiState, { response }) => {
    // Compute template configs OUTSIDE of Immer's produce to avoid frozen object issues
    const clonedResponse = JSON.parse(JSON.stringify(response))
    const uiFlowTemplateConfigs = UiFlowDesignerService.mapAiUiFlowToTemplateConfigs(
      clonedResponse
    ) as any

    return produce(state, (draft) => {
      draft.generatedUiFlow = clonedResponse
      draft.generatedUiFlowTemplateConfigs = uiFlowTemplateConfigs
      // Also update generatedUINew so the draftTemplateConfig selector picks it up
      // and the preview renders via the same path as the regular UI Designer
      draft.generatedUINew = {
        interface: clonedResponse.interface,
        components: clonedResponse.components,
      } as any
      draft.generatingUi = true
      draft.pendingAiResponse = false
    })
  }),
  on(AiDesignerActions.keepUiFlowDraft, (state: AiState) =>
    produce(state, (draft) => {
      draft.pendingKeepDraftSave = true
    })
  ),
  on(AiDesignerActions.keepUiFlowDraftSuccess, (state: AiState) =>
    produce(state, (draft) => {
      // Add user message to chat (same as UI Designer keep flow)
      draft.messages.push({
        message: 'Keep the generated UI Flow.',
        type: MessageType.UserMsg,
        commited: true,
      })
      // Clear all draft state
      draft.pendingKeepDraftSave = false
      draft.generatedUI = null
      draft.generatedUINew = null
      draft.generatedUiFlow = null
      draft.generatedUiFlowTemplateConfigs = null
      draft.temporaryUI = null
      draft.generatedEditDiff = null
      draft.editDiffAppliedConfig = null
      draft.generatingUi = false
      draft.selectedUiFlowPageIndex = 0
      draft.isAiInteractionAllowed = true
      draft.backgroundAiResponse = false
    })
  ),
  on(AiDesignerActions.keepUiFlowDraftFailure, (state: AiState, { errorMsg }) =>
    produce(state, (draft) => {
      draft.pendingKeepDraftSave = false
      draft.messages.push({
        message: `Failed to save the UI Flow. ${errorMsg?.error?.detail || 'Please try again.'}`,
        type: MessageType.ErrorMsg,
        commited: true,
      })
    })
  ),
  on(AiDesignerActions.discardUiFlowDraft, (state: AiState) =>
    produce(state, (draft) => {
      draft.messages.push({
        message: 'Discard the generated UI Flow.',
        type: MessageType.UserMsg,
        commited: true,
      })
      draft.generatedUiFlow = null
      draft.generatedUiFlowTemplateConfigs = null
      draft.generatedUINew = null
      draft.selectedUiFlowPageIndex = 0
      draft.isAiInteractionAllowed = true
      draft.backgroundAiResponse = false
      draft.generatingUi = false
    })
  ),
  on(AiDesignerActions.subtreeEditUiFlowStream, (state: AiState, { selectedTemplateConfigId }) =>
    produce(state, (draft) => {
      draft.selectedUiFlowSubtreeId = selectedTemplateConfigId
    })
  ),
  on(AiActions.selectUiFlowPage, (state: AiState, { pageIndex }) =>
    produce(state, (draft) => {
      draft.selectedUiFlowPageIndex = pageIndex
    })
  )
)

function canSelectAgent(state: AiState): boolean {
  const { selectedAgent, pendingAiResponse, pendingKeepDraftSave, messages } = state

  const isLoading = pendingAiResponse || pendingKeepDraftSave
  const isChangeableAgent =
    !selectedAgent || selectedAgent === AiAgent.ASSISTANT || hasOnlySystemMsg(messages)

  return !isLoading && isChangeableAgent
}

function hasEdits(draft: WritableDraft<AiState>): boolean {
  return (
    !draft.generatedBusinessRule ||
    !draft.generatedFixWarningsBusinessRule ||
    !draft.generatedExpression ||
    !draft.generatedProcess ||
    !draft.generatedUI ||
    !draft.generatedUINew ||
    !draft.generatedEnumeration ||
    !draft.generatedNotification
  )
}

function hasOnlySystemMsg(messages: Message[]): boolean {
  return !messages.find(({ type }) => type !== MessageType.SystemMsg)
}

function openChat(
  draft: WritableDraft<AiState>,
  state: {
    selectedAgent: AiAgent
    initialMessage?: Message
    isChatConversationAllowed?: boolean
    isFileUploadAllowed?: boolean
  }
): void {
  const { selectedAgent, initialMessage, isChatConversationAllowed, isFileUploadAllowed } = state
  draft.selectedAgent = selectedAgent
  draft.sessionId = uuidV4()
  draft.messages = initialMessage ? [initialMessage] : []
  if (typeof isChatConversationAllowed !== 'undefined') {
    draft.isChatConversationAllowed = isChatConversationAllowed
  }
  if (typeof isFileUploadAllowed !== 'undefined') {
    draft.isFileUploadAllowed = isFileUploadAllowed
  }
}

function openChatWithDocumentGenerationMsg(
  draft: WritableDraft<AiState>,
  state: {
    selectedAgent: AiAgent
  }
): void {
  const initialMessage = {
    message: `Specify what you want to generate. ${draft.permissions.canViewDocuments ? 'You can also start from context documents.' : ''}`,
    actions: {
      checkDocumentContext: draft.permissions.canViewDocuments,
      imageUpload:
        state.selectedAgent !== AiAgent.DEVELOPER && draft.permissions.canUploadDocuments,
      processUpload: state.selectedAgent === AiAgent.ANALYST,
    },
    type: MessageType.SystemMsg,
  }

  openChat(draft, { ...state, initialMessage })
}

function closeChat(draft: WritableDraft<AiState>): void {
  newChatSession(draft)

  draft.selectedAgent = null
  draft.sessionId = null
  // draft.messages = [] BR - temporary fix
}

function newChatSession(draft: WritableDraft<AiState>): void {
  draft.selectedDocument = null
  draft.selectedDocumentContext = null
  draft.selectedDesignerUserTaskAreaId = null
  draft.selectedDesignerTemplateConfigId = null
  draft.sessionId = uuidV4()
  draft.isAiInteractionAllowed = true
  draft.isFileUploadAllowed = true
  draft.isChatConversationAllowed = true
  draft.uploadedFiles = []
  draft.generatedProcess = null
  draft.temporaryProcess = null
  draft.startedFromSequence = null
  draft.generatedSequenceProcess = null
  draft.generatedProcessEntities = null
  draft.temporaryProcessEntities = null
  draft.generatedProcessPreviewHeader = null
  draft.generatedEnumeration = null
  draft.currentEnumeration = null
  draft.currentNotification = null
  draft.generatedUI = null
  draft.generatedUINew = null
  draft.temporaryUI = null
  draft.generatedUIDataModelChanges = null
  draft.generatedBusinessRule = null
  draft.generatedTestData = null
  draft.generatedFixWarningsBusinessRule = null
  draft.generatedFixErrorBusinessRule = null
  draft.generatedExpression = null
  draft.generatedImprovedCode = null
  draft.generatedAddComment = null
  draft.draftMessage = null
  draft.initialMessage = null
  draft.isUploadingDocuments = false
}

function generateChatErrorMessage(errorMsg, statusCode: number = 400): Message {
  const message = extractMessageFromError(errorMsg, statusCode)

  return {
    message,
    type: MessageType.ErrorMsg,
    actions: {
      startNewSession: true,
    },
    commited: true,
  }
}

function extractMessageFromError(
  error:
    | string
    | { title: string; defaultMessage: string; error_message: { client_message: string } },
  statusCode: number = 400
): string {
  const CONTINUE_MESSAGE = `You can continue the conversation or start a new chat.`

  if (statusCode >= 500 || statusCode === 0) {
    return `Something went wrong on our end. ${CONTINUE_MESSAGE}`
  } else {
    const DEFAULT_MESSAGE = `Something went wrong with your request. Please check the information you entered and try again.`

    if (typeof error === 'string') {
      return `${error} ${CONTINUE_MESSAGE}`
    }

    const serverMessage =
      error?.title ?? error?.defaultMessage ?? error?.error_message?.client_message

    return `${serverMessage ?? DEFAULT_MESSAGE} ${CONTINUE_MESSAGE}`
  }
}
